"use client";
import { useState, useEffect } from "react";
import { CaseStudy } from "@/types/casestudy";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import Link from "next/link";
import { DeleteDialog } from "../Common/DeleteDialog";

const DraftBlogsPage = () => {
  const [caseStudies, setCaseStudies] = useState<CaseStudy[]>([]);
  const [deploying, setDeploying] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDrafts = async () => {
      const token = localStorage.getItem("yardstickAccessToken") || "";
      try {
        const response = await fetch("/api/case-studies", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const res = await response.json();

        if (!response.ok) {
          console.log(response);
          if (response.status === 401) {
            toast.error(res.error);
            return;
          }
          throw new Error(res.error);
        }

        setCaseStudies(res.data);
      } catch (e) {
        console.log(e);
        setError("Couldn't fetch drafts.");
        toast.error(`Error: ${e.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchDrafts();
  }, []);

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      const response = await fetch("/api/deploy", {
        method: "POST",
      });
      const data = await response.json();

      if (response.ok) {
        console.log(data.message);
        toast.success("Deployment triggered successfully");
      } else {
        toast.error(`Deployment failed: ${data.error}`);
      }
    } catch (error) {
      console.log(error);
      toast.error(`An error occurred: ${(error as Error).message}`);
    } finally {
      setDeploying(false);
    }
  };

  const createSampleCaseStudy = async () => {
    const token = localStorage.getItem("yardstickAccessToken") || "";
    setCreating(true);
    try {
      const response = await fetch("/api/case-studies/sample", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const res = await response.json();

      if (!response.ok) {
        console.log(response);
        if (response.status === 401) {
          toast.error(res.error);
          return;
        }
        throw new Error(res.error);
      }

      setCaseStudies((p) => [...p, res.data]);
      toast.success("Case study created successfully.", {
        toastId: "case-study-created-success",
      });
    } catch (e) {
      console.log(e);
      toast.error(`Error: ${e.message}`, {
        toastId: "case-study-creation-error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCaseStudyDelete = async (id: string) => {
    try {
      const response = await fetch(`/api/case-studies/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("yardstickAccessToken")}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        toast.success("Case study deleted successfully");
      } else {
        console.log(response);
        if (response.status === 401) {
          toast.error("Error: You're not logged in.");
          return;
        }
        toast.error(`Deletion failed: ${data.error}`);
      }
    } catch (error) {
      console.log(error);
      toast.error(`An error occurred: ${(error as Error).message}`);
    }
  };

  return (
    <div className="flex items-center justify-center">
      <div className="mt-20 flex h-fit max-w-[1500px] flex-col gap-10 p-5 sm:p-20">
        {loading ? (
          <p>Fetching Case Studies...</p>
        ) : error ? (
          <p>{error}</p>
        ) : (
          <div className="flex flex-col gap-10">
            <div className="flex items-center justify-between gap-5 sm:min-w-[575px]">
              <h1 className="text-3xl font-bold sm:text-4xl">Case Studies</h1>
              <button
                className="rounded bg-primary p-2 text-white transition-all duration-300 ease-in-out hover:bg-primary/80 dark:bg-primary1"
                onClick={handleDeploy}
                disabled={deploying}
              >
                {deploying ? "Uploading..." : "Upload To Vercel"}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                className="rounded bg-primary p-2 text-white transition-all duration-300 ease-in-out hover:bg-primary/80 dark:bg-primary1"
                onClick={createSampleCaseStudy}
                disabled={creating}
              >
                {creating ? "Creating..." : "New Case Study"}
              </button>
            </div>

            <table className="mt-5 w-full table-auto border-collapse">
              <thead>
                <tr className="border-b border-body-color dark:border-body-color-dark">
                  <th className="w-[40px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    S.No
                  </th>
                  <th className="px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Title
                  </th>
                  <th className="w-[125px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Category
                  </th>
                  <th className="w-[70px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Status
                  </th>
                  <th className="w-[70px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody>
                {caseStudies.map((caseStudy, i) => (
                  <tr
                    key={caseStudy.id}
                    className="border-b border-body-color text-sm font-light hover:bg-purple-900 hover:text-white dark:border-body-color-dark"
                  >
                    <td className="px-2 py-3 dark:text-white sm:py-4">
                      {i + 1}
                    </td>
                    <td className="px-2 py-3 text-xs dark:text-white">
                      <Link
                        href={`/edit-case-studies/${caseStudy.id}`}
                        passHref
                      >
                        <span className="text-left">{caseStudy.title}</span>
                      </Link>
                    </td>
                    <td className="px-2 py-3 text-xs dark:text-white">
                      {caseStudy.category}
                    </td>
                    <td
                      className={`px-2 py-3 text-xs ${caseStudy.status === "Draft" ? "text-red-500" : caseStudy.status === "InReview" ? "text-blue-500" : "text-green-500"}`}
                    >
                      {caseStudy.status === "InReview"
                        ? "In Review"
                        : caseStudy.status}
                    </td>
                    <td className="flex justify-center px-2 py-3 text-red-500 sm:py-4">
                      <DeleteDialog
                        item={"case study"}
                        onDelete={() => handleCaseStudyDelete(caseStudy.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ToastContainer />
      </div>
    </div>
  );
};

export default DraftBlogsPage;
